"use client";

/**
 * Schedule Item Display
 * Renders the content of a single schedule item: time badge, title, speaker, location, vendor, labels, tags.
 * Matches the V1 accordion layout with a colored time badge on the left.
 */

import { useMemo } from "react";
import { Clock, MapPin, User, Award, Check } from "lucide-react";
import { FetchPill } from "@/fetch-ui/FetchPill";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { formatScheduleTime, formatScheduleDateTime, getItemLabels } from "../../../../_logic/utils/schedule-utils";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";
import { isScheduleItemSignupClosed } from "../../../../_logic/utils/activity-signup-status";

interface ScheduleItemDisplayProps {
  item: ScheduleItemRow;
  labels?: ScheduleLabelRow[];
  /** Primary color for the time badge background */
  primaryColor?: string;
  /** When true, shows the item's date inline (use when the group header doesn't cover the date) */
  showDate?: boolean;
  /** When true, uses smaller text sizing for settings tab cards */
  compact?: boolean;
  /** Page-wide IANA timezone for rendering times. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /**
   * Viewer's confirmed registrations: `slotIds` (used when the schedule item
   * is pinned to a specific time slot via `activity_time_slot_id`) and
   * `activityIds` (fallback when the item links to an activity but no
   * specific slot). `null` means no logged-in attendee — no registration
   * affordance renders at all.
   */
  viewerRegistrations?: ViewerRegistrationKeys | null;
}

/** Displays the details of a single schedule item (time badge, title, speaker, location, vendor, labels, tags) */
export function ScheduleItemDisplay({
  item,
  labels = [],
  primaryColor,
  showDate,
  compact,
  timeZone,
  isPublic = false,
  viewerRegistrations = null,
}: ScheduleItemDisplayProps) {
  const registrationState = getScheduleItemRegistrationState(item, viewerRegistrations);
  const isRegistered = registrationState === "registered";
  const isWaitlisted = registrationState === "waitlisted";
  const isSignupClosed = isScheduleItemSignupClosed(item);
  // Show the affordance to logged-in viewers (Registered/Waitlisted/Sign up) AND, when signups are
  // closed, to everyone — so a closed activity reads "Signups closed" the same as the Activities card.
  const showRegistrationAffordance = item.activity_id != null && (viewerRegistrations != null || isSignupClosed);
  // Derived values
  const dateTimeDisplay = useMemo(
    () =>
      showDate
        ? formatScheduleDateTime(item.date, item.start_time, item.end_time, timeZone)
        : formatScheduleTime(item.start_time, item.end_time, { timeZone, date: item.date }),
    [showDate, item.date, item.start_time, item.end_time, timeZone]
  );

  const itemLabels = useMemo(() => getItemLabels(item.label_ids, labels), [labels, item.label_ids]);

  const responsiveRowClass = isPublic
    ? "sm:flex-row sm:items-start sm:gap-4"
    : "@sm:flex-row @sm:items-start @sm:gap-4";
  const badgeClass = isPublic ? "sm:min-w-[130px] sm:justify-center" : "@sm:min-w-[130px] @sm:justify-center";
  return (
    <div className={`flex flex-col gap-1.5 ${primaryColor ? responsiveRowClass : ""}`}>
      {/* Date/time badge with primaryColor */}
      {primaryColor && (
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 self-start rounded-full px-4 py-1.5 text-sm font-semibold ${badgeClass}`}
          style={{ backgroundColor: primaryColor, color: getButtonTextColor(primaryColor) }}
        >
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {dateTimeDisplay}
        </span>
      )}

      {/* Date/time as inline text when no primaryColor (cards/carousel views) */}
      {!primaryColor && (
        <div className="flex items-center gap-1 text-xs font-medium text-indigo-600">
          <Clock className={compact ? "h-3.5 w-3.5 shrink-0" : "h-3 w-3 shrink-0"} aria-hidden="true" />
          {dateTimeDisplay}
        </div>
      )}

      {/* Details */}
      <div className={`flex flex-1 flex-col ${compact ? "gap-1" : "gap-1.5"}`}>
        <p className={compact ? "text-sm font-medium text-gray-900" : "text-base font-semibold text-gray-900"}>
          {item.title}
        </p>
        {item.location && (
          <p
            className={
              compact ? "truncate text-xs font-semibold text-gray-700" : "truncate text-sm font-medium text-gray-600"
            }
          >
            <MapPin className="mr-1 inline-block h-3.5 w-3.5 align-[-3px]" aria-hidden="true" />
            {item.location}
          </p>
        )}
        {item.speaker && (
          <div
            className={
              compact
                ? "flex items-center gap-1 text-xs font-semibold text-gray-700"
                : "flex items-center gap-1.5 text-sm font-medium text-gray-600"
            }
          >
            <User className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{item.speaker}</span>
          </div>
        )}
        {item.vendor && (
          <div
            className={
              compact
                ? "flex items-center gap-1 text-xs font-semibold text-gray-700"
                : "flex items-center gap-1.5 text-sm font-medium text-gray-600"
            }
          >
            <Award className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{item.vendor}</span>
          </div>
        )}

        {/* Labels + registration affordance */}
        {(itemLabels.length > 0 || showRegistrationAffordance) && (
          <div className="flex flex-wrap gap-1">
            {itemLabels.map((label) => (
              <FetchPill key={label.id} as="span" size="sm" color={label.color || undefined} selected>
                {label.name}
              </FetchPill>
            ))}
            {showRegistrationAffordance &&
              (isRegistered ? (
                <FetchPill as="span" size="sm" color="#10b981" selected>
                  <Check className="mr-1 h-3 w-3" aria-hidden="true" />
                  Registered
                </FetchPill>
              ) : isWaitlisted ? (
                <FetchPill as="span" size="sm" color="#f59e0b" selected>
                  Waitlisted
                </FetchPill>
              ) : isSignupClosed ? (
                <FetchPill as="span" size="sm">
                  Signups closed
                </FetchPill>
              ) : (
                <FetchPill as="span" size="sm">
                  Sign up
                </FetchPill>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
